/** CRA only injects env vars that start with REACT_APP_. `BACKEND_URL` without that prefix is ignored. */
function normalizeApiBaseUrl(value) {
  if (!value || !String(value).trim()) return null;
  let u = String(value).trim().replace(/\/+$/, '');
  if (/\/api$/i.test(u)) return u;
  return `${u}/api`;
}

/**
 * When the page is opened via a LAN/IP host (e.g. from a phone at
 * http://192.168.1.32:3001), a loopback API host baked at build time points at
 * the *device*, not the dev machine. Swap loopback → page host so the same
 * build works both from localhost and from the LAN.
 */
function hostRelativeToPage(base) {
  if (!base) return base;
  try {
    if (typeof window === 'undefined' || !window.location || !window.location.hostname) {
      return base;
    }
    const pageHost = window.location.hostname;
    const isLoopback = (h) => h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '[::1]';
    if (isLoopback(pageHost)) return base;
    const u = new URL(base);
    if (isLoopback(u.hostname)) {
      u.hostname = pageHost;
      return u.toString().replace(/\/+$/, '');
    }
  } catch (e) {
    /* not a parseable absolute URL — leave as-is */
  }
  return base;
}

const getApiBaseUrl = () => {
  const explicit = process.env.REACT_APP_API_BASE_URL;
  if (explicit && String(explicit).trim()) {
    return hostRelativeToPage(String(explicit).trim().replace(/\/+$/, ''));
  }
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fromBackend = normalizeApiBaseUrl(backend);
  if (fromBackend) return hostRelativeToPage(fromBackend);

  /**
   * Development and production are both same-origin: the browser calls `/api` on
   * the page origin, so one build works from localhost, a LAN IP, or a tunnel.
   *   - dev:  src/setupProxy.js forwards `/api` and `/uploads` to REACT_APP_PROXY_TARGET
   *   - prod: nginx forwards `/api` and `/uploads` to the backend
   * Set REACT_APP_API_BASE_URL (above) only to bypass the proxy and hit a fixed host.
   */
  return '/api';
};

export const API_BASE_URL = getApiBaseUrl();

export const apiUrl = (path) => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const base = API_BASE_URL.replace(/\/+$/, '');
  if (!base) {
    return `/api/${cleanPath}`;
  }
  if (base.endsWith('/api')) {
    return `${base}/${cleanPath}`;
  }
  return `${base}/api/${cleanPath}`;
};

const getBackendOrigin = () => {
  const base = API_BASE_URL.replace(/\/api\/?$/, '').trim();
  if (base) return base;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

export const getImageDisplayUrl = (url) => {
  if (typeof url !== 'string' || !url.trim()) return '';
  const u = url.trim();
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) return getBackendOrigin() + u;
  return getBackendOrigin() + '/' + u.replace(/^\//, '');
};

const API_KEY = process.env.REACT_APP_API_KEY || '';

const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';

let onSessionExpired = null;
export const setOnSessionExpired = (handler) => {
  onSessionExpired = handler;
};
export const notifySessionExpired = () => {
  if (typeof onSessionExpired === 'function') onSessionExpired();
};

export const getStoredAccessToken = () => {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
};
export const getStoredRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};
export const setStoredTokens = (accessToken, refreshToken) => {
  try {
    if (accessToken != null) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken != null) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch (e) {
    /* storage unavailable — ignore */
  }
};
export const clearStoredTokens = () => {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (e) {
    /* storage unavailable — ignore */
  }
};

export const getPublicJsonHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (API_KEY) headers['X-API-Key'] = API_KEY;
  return headers;
};

/**
 * Preferred access token: the stored one (kept fresh by refresh) over the
 * passed arg, so headers rebuilt after a silent refresh use the new token.
 */
export const getHeaders = (token) => {
  const headers = { ...getPublicJsonHeaders() };
  const accessToken = getStoredAccessToken() || token;
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  return headers;
};

/** Abort slow requests (mobile networks, hung proxies). */
export const API_FETCH_TIMEOUT_MS = 30000;

let refreshPromise = null;

/**
 * Single-flight refresh: exchange the stored refresh token for a fresh
 * access token (+ rotated refresh token). Resolves true on success.
 */
export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) return false;
    try {
      const res = await fetch(apiUrl('/auth/refresh-token'), {
        method: 'POST',
        headers: getPublicJsonHeaders(),
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        clearStoredTokens();
        return false;
      }
      const data = await res.json();
      const d = data && data.data;
      const access = d && d.token;
      const nextRefresh = d && d.refreshToken;
      if (typeof access !== 'string' || typeof nextRefresh !== 'string') {
        clearStoredTokens();
        return false;
      }
      setStoredTokens(access, nextRefresh);
      return true;
    } catch (e) {
      return false;
    }
  })();
  refreshPromise.finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

const rawFetchWithTimeout = async (input, init) => {
  const controller = new AbortController();
  const { signal: _ignored, ...rest } = init;
  const timer = setTimeout(() => controller.abort(), API_FETCH_TIMEOUT_MS);
  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Core request wrapper: transparently refreshes once on 401 and retries, so an
 * expired 15-minute access token never surfaces to the user. On refresh failure
 * it flags the session as expired (the app redirects to login).
 */
export async function apiFetch(input, init = {}) {
  let response = await rawFetchWithTimeout(input, init);

  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const accessToken = getStoredAccessToken();
      const headers = new Headers(init.headers || {});
      if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
      const nextInit = { ...init, headers };
      const retried = await rawFetchWithTimeout(input, nextInit);
      if (retried.status !== 401) return retried;
    }
  }

  if (response.status === 401 && getStoredAccessToken()) {
    clearStoredTokens();
    notifySessionExpired();
  }

  return response;
}

if (typeof process !== 'undefined' && process.env.NODE_ENV === 'production' && !API_KEY) {
  console.warn(
    '[Nezoola] REACT_APP_API_KEY is unset. The production API returns 503 if API keys are required.'
  );
}
